/**
 * NewsTab Component
 * 
 * Live news aggregator + staff-authored articles.
 * Fetches RSS/JSON from MTGGoldfish, Reddit, EDHRec, Scryfall via edge function.
 * Displays both external feed articles and internal Supabase articles.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Newspaper, TrendingUp, Package, Code, Users, Trophy,
    Clock, ChevronRight, ExternalLink, Star, Filter, RefreshCw,
    Loader2, ChevronDown, X, Plus, Rss, ArrowUpRight, MessageSquare,
    ThumbsUp, Globe, Fish, Search, Swords, Layers, Mountain, Crown
} from 'lucide-react';
import { supabase, authService, roleService } from '../lib/supabase';
import { SubNavDropdown } from './SubNavDropdown';

// ─── Types ───
interface NewsArticle {
    id: string;
    title: string;
    category: 'meta' | 'set' | 'patch' | 'community' | 'tournament';
    author_name: string;
    published_at: string;
    summary: string;
    content?: string;
    thumbnail_url?: string;
    tags: string[];
    featured: boolean;
    is_published: boolean;
}

interface FeedArticle {
    id: string;
    title: string;
    summary: string;
    url: string;
    author: string;
    source: string;
    source_icon: string;
    source_color: string;
    published_at: string;
    thumbnail_url: string | null;
    category: string;
    upvotes?: number;
    comments?: number;
}

// ─── Category configuration ───
const CATEGORIES = {
    meta: {
        label: 'Meta Update',
        icon: TrendingUp,
        color: 'text-purple-400',
        bgColor: 'bg-purple-500/10',
        borderColor: 'border-purple-500/30',
    },
    set: {
        label: 'Set Release',
        icon: Package,
        color: 'text-blue-400',
        bgColor: 'bg-blue-500/10',
        borderColor: 'border-blue-500/30',
    },
    patch: {
        label: 'Patch Notes',
        icon: Code,
        color: 'text-green-400',
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500/30',
    },
    community: {
        label: 'Community',
        icon: Users,
        color: 'text-orange-400',
        bgColor: 'bg-orange-500/10',
        borderColor: 'border-orange-500/30',
    },
    tournament: {
        label: 'Tournament',
        icon: Trophy,
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-500/10',
        borderColor: 'border-yellow-500/30',
    },
};

// ─── Source filter config ───
const SOURCE_FILTERS: { key: string; label: string; Icon: React.FC<{ className?: string }>; color: string }[] = [
    { key: 'all', label: 'All Sources', Icon: Newspaper, color: 'text-white' },
    { key: 'MTGGoldfish', label: 'MTGGoldfish', Icon: Fish, color: 'text-yellow-300' },
    { key: 'MTGRocks', label: 'MTGRocks', Icon: Mountain, color: 'text-fuchsia-300' },
    { key: 'r/magicTCG', label: 'r/magicTCG', Icon: MessageSquare, color: 'text-orange-300' },
    { key: 'r/EDH', label: 'r/EDH', Icon: Crown, color: 'text-violet-300' },
    { key: 'EDHRec', label: 'EDHRec', Icon: Swords, color: 'text-green-300' },
    { key: 'Scryfall', label: 'Scryfall', Icon: Search, color: 'text-blue-300' },
    { key: 'DECKSTR', label: 'DECKSTR', Icon: Layers, color: 'text-purple-300' },
];

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getSourceGradient(source: string): string {
    switch (source) {
        case 'MTGGoldfish': return 'from-yellow-900/30 to-yellow-950/10';
        case 'MTGRocks': return 'from-fuchsia-900/30 to-fuchsia-950/10';
        case 'r/magicTCG': return 'from-orange-900/30 to-orange-950/10';
        case 'r/EDH': return 'from-violet-900/30 to-violet-950/10';
        case 'EDHRec': return 'from-green-900/30 to-green-950/10';
        case 'Scryfall': return 'from-blue-900/30 to-blue-950/10';
        default: return 'from-purple-900/30 to-purple-950/10';
    }
}

function getSourceBorder(source: string): string {
    switch (source) {
        case 'MTGGoldfish': return 'border-yellow-500/20 hover:border-yellow-500/40';
        case 'MTGRocks': return 'border-fuchsia-500/20 hover:border-fuchsia-500/40';
        case 'r/magicTCG': return 'border-orange-500/20 hover:border-orange-500/40';
        case 'r/EDH': return 'border-violet-500/20 hover:border-violet-500/40';
        case 'EDHRec': return 'border-green-500/20 hover:border-green-500/40';
        case 'Scryfall': return 'border-blue-500/20 hover:border-blue-500/40';
        default: return 'border-purple-500/20 hover:border-purple-500/40';
    }
}

function getSourceTextColor(source: string): string {
    switch (source) {
        case 'MTGGoldfish': return 'text-yellow-400';
        case 'MTGRocks': return 'text-fuchsia-400';
        case 'r/magicTCG': return 'text-orange-400';
        case 'r/EDH': return 'text-violet-400';
        case 'EDHRec': return 'text-green-400';
        case 'Scryfall': return 'text-blue-400';
        default: return 'text-purple-400';
    }
}

function getSourceIcon(source: string): React.FC<{ className?: string }> {
    switch (source) {
        case 'MTGGoldfish': return Fish;
        case 'MTGRocks': return Mountain;
        case 'r/magicTCG': return MessageSquare;
        case 'r/EDH': return Crown;
        case 'EDHRec': return Swords;
        case 'Scryfall': return Search;
        default: return Layers;
    }
}

// ============================================
// FEED ARTICLE CARD
// ============================================
function FeedCard({ article }: { article: FeedArticle }) {
    const sourceColor = getSourceTextColor(article.source);
    const gradient = getSourceGradient(article.source);
    const border = getSourceBorder(article.source);
    const SourceIcon = getSourceIcon(article.source);

    return (
        <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`group relative block p-4 rounded-xl border bg-gradient-to-br ${gradient} ${border} transition-all duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-black/20`}
        >
            {/* Source badge + time */}
            <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-1.5">
                    <SourceIcon className={`w-3.5 h-3.5 ${sourceColor}`} />
                    <span className={`text-[11px] font-semibold ${sourceColor}`}>{article.source}</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-white/35">
                    <Clock className="w-2.5 h-2.5" />
                    {formatDate(article.published_at)}
                </div>
            </div>

            {/* Thumbnail + Title + Summary */}
            <div className="flex gap-3">
                {article.thumbnail_url && (
                    <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-white/5">
                        <img
                            src={article.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white group-hover:text-white/90 leading-tight mb-1 line-clamp-2">
                        {article.title}
                    </h3>
                    <p className="text-[11px] text-white/45 leading-relaxed line-clamp-2">
                        {article.summary}
                    </p>
                </div>
            </div>

            {/* Footer: author + stats */}
            <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/5">
                <span className="text-[10px] text-white/30 truncate max-w-[200px]">{article.author}</span>
                <div className="flex items-center gap-3">
                    {article.upvotes !== undefined && article.upvotes > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-orange-400/70">
                            <ThumbsUp className="w-2.5 h-2.5" />
                            {article.upvotes.toLocaleString()}
                        </span>
                    )}
                    {article.comments !== undefined && article.comments > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-white/30">
                            <MessageSquare className="w-2.5 h-2.5" />
                            {article.comments}
                        </span>
                    )}
                    <ArrowUpRight className="w-3 h-3 text-white/20 group-hover:text-white/50 transition-colors" />
                </div>
            </div>
        </a>
    );
}

// ============================================
// CREATE/EDIT ARTICLE MODAL (Staff Only)
// ============================================
interface ArticleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved: (article: NewsArticle) => void;
    editArticle?: NewsArticle | null;
}

function ArticleModal({ isOpen, onClose, onSaved, editArticle }: ArticleModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [title, setTitle] = useState('');
    const [category, setCategory] = useState<NewsArticle['category']>('patch');
    const [summary, setSummary] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState('');
    const [featured, setFeatured] = useState(false);

    useEffect(() => {
        if (editArticle) {
            setTitle(editArticle.title);
            setCategory(editArticle.category);
            setSummary(editArticle.summary);
            setContent(editArticle.content || '');
            setTags(editArticle.tags?.join(', ') || '');
            setFeatured(editArticle.featured);
        } else {
            setTitle('');
            setCategory('patch');
            setSummary('');
            setContent('');
            setTags('');
            setFeatured(false);
        }
    }, [editArticle, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('Not authenticated');

            const articleData = {
                title,
                category,
                summary,
                content: content || null,
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
                featured,
                author_name: 'DECKSTR Team',
                is_published: true,
            };

            if (editArticle) {
                const { data, error: updateError } = await supabase
                    .from('news_articles')
                    .update(articleData)
                    .eq('id', editArticle.id)
                    .select()
                    .single();

                if (updateError) throw updateError;
                onSaved(data);
            } else {
                const { data, error: insertError } = await supabase
                    .from('news_articles')
                    .insert(articleData)
                    .select()
                    .single();

                if (insertError) throw insertError;
                onSaved(data);
            }

            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to save article');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white">
                        {editArticle ? 'Edit Article' : 'New Article'}
                    </h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white p-1">
                        <X className="size-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-white/60 mb-1">Title *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm text-white/60 mb-1">Category *</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value as NewsArticle['category'])}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                            >
                                {Object.entries(CATEGORIES).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={featured}
                                    onChange={(e) => setFeatured(e.target.checked)}
                                    className="rounded border-white/20 bg-white/5 text-purple-500"
                                />
                                Featured Article
                            </label>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm text-white/60 mb-1">Summary *</label>
                        <textarea
                            value={summary}
                            onChange={(e) => setSummary(e.target.value)}
                            required
                            rows={2}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-white/60 mb-1">Full Content (Markdown)</label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            rows={6}
                            placeholder="Optional full article content..."
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500 resize-none font-mono text-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-white/60 mb-1">Tags (comma separated)</label>
                        <input
                            type="text"
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="standard, meta, aetherdrift"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500"
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-white/60 hover:text-white">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !title || !summary}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white rounded-lg"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editArticle ? 'Update' : 'Publish'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ============================================
// INTERNAL (DECKSTR) NEWS CARD
// ============================================
interface NewsCardProps {
    article: NewsArticle;
    featured?: boolean;
    onEdit?: () => void;
    isStaff?: boolean;
}

function NewsCard({ article, featured = false, onEdit, isStaff }: NewsCardProps) {
    const categoryConfig = CATEGORIES[article.category];
    const Icon = categoryConfig.icon;
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            className={`group relative p-4 rounded-xl border transition-all duration-200 bg-gradient-to-br from-purple-900/30 to-purple-950/10 ${featured
                ? 'border-purple-500/30 hover:border-purple-500/50 md:col-span-2'
                : 'border-purple-500/20 hover:border-purple-500/40'
                } hover:scale-[1.01] hover:shadow-lg hover:shadow-black/20`}
        >
            {/* Featured badge */}
            {featured && (
                <div className="absolute -top-2 -right-2 px-2 py-1 bg-yellow-500/90 text-black text-xs font-bold rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    Featured
                </div>
            )}

            {/* Staff Edit Button */}
            {isStaff && onEdit && (
                <button
                    onClick={onEdit}
                    className="absolute top-2 right-2 px-2 py-1 bg-white/10 hover:bg-white/20 text-white/60 text-xs rounded transition-colors"
                >
                    Edit
                </button>
            )}

            {/* Source + Category & Date */}
            <div className="flex items-center gap-2 mb-2.5">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm">🎴</span>
                    <span className="text-[11px] font-semibold text-purple-400">DECKSTR</span>
                </div>
                <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full ${categoryConfig.bgColor} ${categoryConfig.borderColor} border`}>
                    <Icon className={`w-2.5 h-2.5 ${categoryConfig.color}`} />
                    <span className={`text-[10px] font-medium ${categoryConfig.color}`}>{categoryConfig.label}</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-white/35 ml-auto">
                    <Clock className="w-2.5 h-2.5" />
                    {formatDate(article.published_at)}
                </div>
            </div>

            {/* Title */}
            <h3 className={`font-semibold text-white group-hover:text-purple-200 transition-colors mb-1.5 leading-tight ${featured ? 'text-base' : 'text-sm'}`}>
                {article.title}
            </h3>

            {/* Summary */}
            <p className={`text-white/50 mb-2 ${featured ? 'text-xs' : 'text-[11px]'} ${!expanded ? 'line-clamp-3' : ''} leading-relaxed`}>
                {article.summary}
            </p>

            {/* Content (expandable) */}
            {article.content && (
                <div>
                    {expanded && (
                        <div className="text-xs text-white/60 mb-2 whitespace-pre-wrap border-t border-white/10 pt-2">
                            {article.content}
                        </div>
                    )}
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="flex items-center gap-1 text-[11px] text-purple-400 hover:text-purple-300"
                    >
                        {expanded ? 'Show less' : 'Read more'}
                        <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                    </button>
                </div>
            )}

            {/* Tags */}
            {article.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {article.tags.slice(0, 4).map(tag => (
                        <span key={tag} className="text-[10px] text-white/35 bg-white/5 px-1.5 py-0.5 rounded-full">
                            #{tag}
                        </span>
                    ))}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-white/5">
                <span className="text-[10px] text-white/30">By {article.author_name}</span>
            </div>
        </div>
    );
}

// ============================================
// MAIN NEWS TAB
// ============================================
export function NewsTab() {
    // Internal articles (Supabase)
    const [articles, setArticles] = useState<NewsArticle[]>([]);
    const [loadingArticles, setLoadingArticles] = useState(true);

    // External feed articles
    const [feedArticles, setFeedArticles] = useState<FeedArticle[]>([]);
    const [loadingFeed, setLoadingFeed] = useState(true);
    const [feedError, setFeedError] = useState<string | null>(null);
    const [feedFetchedAt, setFeedFetchedAt] = useState<string | null>(null);

    // Filters
    const [selectedSource, setSelectedSource] = useState<string>('all');

    // Staff
    const [isStaff, setIsStaff] = useState(false);
    const [showArticleModal, setShowArticleModal] = useState(false);
    const [editingArticle, setEditingArticle] = useState<NewsArticle | null>(null);

    // Check staff permissions
    useEffect(() => {
        const checkStaff = async () => {
            const user = await authService.getCurrentUser();
            if (user) {
                const role = await roleService.getCurrentUserRole();
                setIsStaff(['admin', 'mod', 'owner'].includes(role || ''));
            }
        };
        checkStaff();
    }, []);

    // Fetch internal articles
    const fetchArticles = useCallback(async () => {
        setLoadingArticles(true);
        try {
            const { data, error: fetchError } = await supabase
                .from('news_articles')
                .select('*')
                .eq('is_published', true)
                .order('published_at', { ascending: false });

            if (fetchError) throw fetchError;
            setArticles(data || []);
        } catch (err: unknown) {
            console.error('Failed to fetch internal articles:', err);
        } finally {
            setLoadingArticles(false);
        }
    }, []);

    // Fetch external feed
    const fetchFeed = useCallback(async () => {
        setLoadingFeed(true);
        setFeedError(null);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const SUPABASE_URL = 'https://yytoqcneczoxwegwtuep.supabase.co';
            const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl5dG9xY25lY3pveHdlZ3d0dWVwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgwODM1MDgsImV4cCI6MjA4MzY1OTUwOH0.AapY3VenT2om6UHhxa5KmM4UxeilQ_DRsct2ubzi1Gs';

            const response = await fetch(`${SUPABASE_URL}/functions/v1/news-aggregator`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
                    'apikey': SUPABASE_ANON_KEY,
                },
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setFeedArticles(data.articles || []);
            setFeedFetchedAt(data.fetched_at);
        } catch (err: unknown) {
            console.error('Failed to fetch news feed:', err);
            setFeedError('Could not load external news. Edge function may not be deployed yet.');
        } finally {
            setLoadingFeed(false);
        }
    }, []);

    useEffect(() => {
        fetchArticles();
        fetchFeed();
    }, [fetchArticles, fetchFeed]);

    // Build unified feed
    const allItems = useMemo(() => {
        const items: Array<{
            type: 'internal' | 'external';
            id: string;
            title: string;
            summary: string;
            source: string;
            published_at: string;
            data: NewsArticle | FeedArticle;
        }> = [];

        // Add internal articles
        articles.forEach(a => {
            items.push({
                type: 'internal',
                id: `internal-${a.id}`,
                title: a.title,
                summary: a.summary,
                source: 'DECKSTR',
                published_at: a.published_at,
                data: a,
            });
        });

        // Add external feed articles
        feedArticles.forEach(f => {
            items.push({
                type: 'external',
                id: f.id,
                title: f.title,
                summary: f.summary,
                source: f.source,
                published_at: f.published_at,
                data: f,
            });
        });

        // Sort by date
        items.sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());

        // Filter by source
        if (selectedSource !== 'all') {
            return items.filter(i => i.source === selectedSource);
        }

        return items;
    }, [articles, feedArticles, selectedSource]);

    const handleEdit = (article: NewsArticle) => {
        setEditingArticle(article);
        setShowArticleModal(true);
    };

    const handleArticleSaved = (saved: NewsArticle) => {
        if (editingArticle) {
            setArticles(prev => prev.map(a => a.id === saved.id ? saved : a));
        } else {
            setArticles(prev => [saved, ...prev]);
        }
        setEditingArticle(null);
    };

    const loading = loadingArticles && loadingFeed;
    const totalSources = new Set(feedArticles.map(f => f.source)).size + (articles.length > 0 ? 1 : 0);

    return (
        <div className="max-w-6xl mx-auto space-y-5 p-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/20 hidden sm:block">
                        <Rss className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-base sm:text-xl font-bold text-white">MTG News Feed</h1>
                        <p className="text-[10px] sm:text-xs text-white/40 hidden sm:block">
                            Live from {totalSources} sources
                            {feedFetchedAt && ` · Updated ${formatDate(feedFetchedAt)}`}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2">
                    {isStaff && (
                        <button
                            onClick={() => {
                                setEditingArticle(null);
                                setShowArticleModal(true);
                            }}
                            className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs font-medium transition-colors border border-purple-500/20"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">New Post</span>
                        </button>
                    )}
                    {/* Source Filter Dropdown */}
                    <SubNavDropdown
                        items={SOURCE_FILTERS.map(src => ({
                            key: src.key,
                            label: src.label,
                            icon: src.Icon,
                            color: src.color,
                            count: src.key === 'all'
                                ? feedArticles.length + articles.length
                                : src.key === 'DECKSTR'
                                    ? articles.length
                                    : feedArticles.filter(f => f.source === src.key).length,
                        }))}
                        activeKey={selectedSource}
                        onSelect={setSelectedSource}
                    />
                    <button
                        onClick={() => { fetchArticles(); fetchFeed(); }}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs font-medium transition-colors border border-purple-500/20"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">Refresh</span>
                    </button>
                </div>
            </div>


            {/* Feed Error */}
            {feedError && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-xs flex items-center gap-2">
                    <Globe className="w-4 h-4 flex-shrink-0" />
                    <span>{feedError}</span>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                        <span className="text-xs text-white/40">Loading feeds...</span>
                    </div>
                </div>
            )}

            {/* Articles Grid */}
            {!loading && allItems.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {allItems.map(item => {
                        if (item.type === 'internal') {
                            const article = item.data as NewsArticle;
                            return (
                                <NewsCard
                                    key={item.id}
                                    article={article}
                                    featured={article.featured}
                                    isStaff={isStaff}
                                    onEdit={() => handleEdit(article)}
                                />
                            );
                        } else {
                            return (
                                <FeedCard
                                    key={item.id}
                                    article={item.data as FeedArticle}
                                />
                            );
                        }
                    })}
                </div>
            )}

            {/* Empty state */}
            {!loading && allItems.length === 0 && (
                <div className="text-center py-16 text-white/40">
                    <Newspaper className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p className="text-sm">No articles found{selectedSource !== 'all' ? ` from ${selectedSource}` : ''}.</p>
                    {selectedSource !== 'all' && (
                        <button
                            onClick={() => setSelectedSource('all')}
                            className="mt-2 text-purple-400 hover:text-purple-300 text-xs"
                        >
                            Show all sources
                        </button>
                    )}
                </div>
            )}

            {/* External Sources — Quick Links */}
            <div className="p-4 bg-gradient-to-br from-slate-800/50 to-slate-900/30 rounded-xl border border-white/5">
                <h3 className="text-xs font-medium text-white/40 mb-3 flex items-center gap-2 uppercase tracking-wider">
                    <ExternalLink className="w-3 h-3" />
                    Quick Links
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                        { name: 'MTGGoldfish', url: 'https://www.mtggoldfish.com/articles', desc: 'Meta, prices, articles', color: 'hover:border-yellow-500/30', textColor: 'group-hover:text-yellow-300' },
                        { name: 'Wizards Official', url: 'https://magic.wizards.com/en/news', desc: 'Bans, announcements', color: 'hover:border-amber-500/30', textColor: 'group-hover:text-amber-300' },
                        { name: 'r/magicTCG', url: 'https://www.reddit.com/r/magicTCG/', desc: 'Community discussions', color: 'hover:border-orange-500/30', textColor: 'group-hover:text-orange-300' },
                        { name: 'EDHRec', url: 'https://edhrec.com/articles', desc: 'Commander meta', color: 'hover:border-green-500/30', textColor: 'group-hover:text-green-300' },
                        { name: 'Scryfall', url: 'https://scryfall.com/sets', desc: 'New sets, spoilers', color: 'hover:border-blue-500/30', textColor: 'group-hover:text-blue-300' },
                        { name: '@wizards_magic', url: 'https://twitter.com/wizards_magic', desc: 'Official Twitter/X', color: 'hover:border-sky-500/30', textColor: 'group-hover:text-sky-300' },
                        { name: '@MagicEsports', url: 'https://twitter.com/MagicEsports', desc: 'Competitive news', color: 'hover:border-purple-500/30', textColor: 'group-hover:text-purple-300' },
                        { name: 'CFB Strategy', url: 'https://channelfireball.com/all-strategy/', desc: 'Pro player articles', color: 'hover:border-red-500/30', textColor: 'group-hover:text-red-300' },
                    ].map(link => (
                        <a
                            key={link.name}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`group p-2.5 bg-white/3 hover:bg-white/5 rounded-lg border border-white/5 ${link.color} transition-all`}
                        >
                            <div className={`text-xs font-medium text-white/70 ${link.textColor} transition-colors`}>{link.name}</div>
                            <div className="text-[10px] text-white/30">{link.desc}</div>
                        </a>
                    ))}
                </div>
            </div>

            {/* Article Modal */}
            <ArticleModal
                isOpen={showArticleModal}
                onClose={() => {
                    setShowArticleModal(false);
                    setEditingArticle(null);
                }}
                onSaved={handleArticleSaved}
                editArticle={editingArticle}
            />
        </div>
    );
}

export default NewsTab;
